import * as _ from "lodash";
import type { OktaAuth } from "@okta/okta-auth-js";
import { jwtDecode } from "jwt-decode";
import axios, { setInterceptorReady, resetInterceptorReady } from "./httpClient";
import { validateOktaToken } from "./validateOktaToken";
import { Log } from "./log";
import { isTokenExpired, redirectToOktaLogin, getUserInfo } from "../helpers";
import { logUserIn } from "../AppState/logUserIn";
import { PopoutNotification } from "../AppState/popoutNotification";
import NOTIFICATION_TYPE from "../AppState/NotificationType";
import type { NavigateFunction } from "react-router-dom";
import type { Dispatch } from "redux";
import {
  TOKEN_CONFIG,
  EXCLUDED_ENDPOINTS,
  DB_USER_ERROR_PATTERNS,
  AUTH_ERROR_MESSAGES,
  STORAGE_KEYS,
  ErrorMessages
} from "../constants";
import { AxiosRequestConfig } from "axios";

// ============================================================================
// State Management
// ============================================================================

let isInterceptorInitialized = false;
let isRefreshingToken = false;
let tokenRefreshPromise: Promise<string> | null = null;
let lastTokenValidationTime = 0;

// Circuit breaker state for network errors
let networkErrorCount = 0;
let circuitBreakerOpenUntil = 0;
let lastNetworkErrorNotificationTime = 0;

const CIRCUIT_BREAKER_THRESHOLD = 3; // Open circuit after 3 consecutive network errors
const CIRCUIT_BREAKER_RESET_TIME = 30000; // 30 seconds before retrying
const ERROR_NOTIFICATION_DEBOUNCE = 5000; // 5 seconds between error notifications

// Store references for use in helper functions
let oktaAuthInstance: OktaAuth | null = null;
let dispatchInstance: Dispatch | null = null;
let navigateInstance: NavigateFunction | null = null;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Check if JWT token has valid structure and can be decoded
 * @param token - JWT token string
 * @returns true if token is structurally valid, false otherwise
 */
const isTokenStructureValid = (token: string): boolean => {
  if (!token) return false;
  
  try {
    const decoded = jwtDecode(token);
    return decoded !== null && decoded !== undefined;
  } catch (error) {
    console.error("Invalid JWT token structure:", error);
    return false;
  }
};

/**
 * Check if endpoint should skip JWT validation
 * Uses pathname only to prevent query param bypass attacks
 * @param url - Request URL
 * @returns true if endpoint is excluded from JWT validation
 */
const isExcludedEndpoint = (url?: string): boolean => {
  if (!url) return false; // Require JWT if URL is missing
  if (!url.includes("/api/v1")) return true;
  
  // Extract pathname only (ignore query params and hash to prevent bypass)
  const pathname = url.split(/[?#]/)[0];
  
  // Check if pathname ends with an excluded endpoint
  return EXCLUDED_ENDPOINTS.some(endpoint => pathname.endsWith(endpoint));
};

/**
 * Check if circuit breaker is open (network errors detected)
 * @returns true if circuit is open and requests should be blocked
 */
const isCircuitBreakerOpen = (): boolean => {
  if (circuitBreakerOpenUntil === 0) return false;
  if (Date.now() >= circuitBreakerOpenUntil) {
    // Reset circuit breaker after timeout
    console.log("Circuit breaker reset - allowing requests again");
    circuitBreakerOpenUntil = 0;
    networkErrorCount = 0;
    return false;
  }
  return true;
};

/**
 * Record a network error and potentially open the circuit breaker
 */
const recordNetworkError = (): void => {
  networkErrorCount++;
  if (networkErrorCount >= CIRCUIT_BREAKER_THRESHOLD) {
    circuitBreakerOpenUntil = Date.now() + CIRCUIT_BREAKER_RESET_TIME;
    console.warn(`Circuit breaker opened after ${networkErrorCount} network errors. Will retry in ${CIRCUIT_BREAKER_RESET_TIME / 1000}s`);
  }
};

/**
 * Reset network error count on successful request
 */
const resetNetworkErrorCount = (): void => {
  if (networkErrorCount > 0) {
    console.log("Network recovered - resetting error count");
    networkErrorCount = 0;
    circuitBreakerOpenUntil = 0;
  }
};

/**
 * Check if error notification should be shown (debounced)
 * @returns true if enough time has passed since last notification
 */
const shouldShowErrorNotification = (): boolean => {
  const now = Date.now();
  if (now - lastNetworkErrorNotificationTime >= ERROR_NOTIFICATION_DEBOUNCE) {
    lastNetworkErrorNotificationTime = now;
    return true;
  }
  return false;
};

/**
 * Check if token validation is cached and still fresh
 * @returns true if validation cache is still valid
 */
const isValidationCacheFresh = (): boolean => {
  if (lastTokenValidationTime === 0) return false;
  const timeSinceLastValidation = Date.now() - lastTokenValidationTime;
  return timeSinceLastValidation < TOKEN_CONFIG.VALIDATION_CACHE_DURATION;
};

/**
 * Clear validation cache (called on visibility change or storage events)
 */
const clearValidationCache = (): void => {
  lastTokenValidationTime = 0;
};


/**
 * Check if user is authenticated with Okta
 * @throws Error if user is not authenticated
 */
const ensureOktaAuthenticated = async (): Promise<void> => {
  if (!oktaAuthInstance) {
    throw new Error("OktaAuth instance not initialized");
  }
  const authState = oktaAuthInstance.authStateManager.getAuthState();
  if (!authState?.isAuthenticated) {
    throw new Error(AUTH_ERROR_MESSAGES.USER_NOT_AUTHENTICATED);
  }
};

/**
 * Handle successful token fetch - store token and update state
 */
const handleTokenFetchSuccess = (user: any): string => {
  localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(user));
  dispatchInstance!(logUserIn(true));
  lastTokenValidationTime = Date.now();
  resetNetworkErrorCount();
  console.log("Successfully fetched and stored new JWT token");
  return user.jwtToken;
};

/**
 * Fetch and store new JWT token (single attempt, no retries)
 * Prevents multiple simultaneous token refresh calls
 * @returns Promise that resolves to JWT token string
 * @throws Error if token fetch fails
 */

const tryFetchToken = async (): Promise<string> => {
  if (isCircuitBreakerOpen()) {
    console.warn("Circuit breaker open - skipping token fetch");
    throw new Error("Server unavailable - circuit breaker open");
  }
  
  console.log("Fetching new JWT token...");
  await ensureOktaAuthenticated();
  
  // Get token directly from Okta tokenManager (not localStorage) to avoid stale data
  const idTokenObj = await oktaAuthInstance!.tokenManager.get('idToken');
  const oktaIdToken = (idTokenObj as any)?.idToken;
  
  if (!oktaIdToken) {
    throw new Error("Failed to get Okta ID token from tokenManager");
  }
  
  // Pass the token directly to avoid localStorage race condition
  const user = await validateOktaToken(oktaIdToken);
  return handleTokenFetchSuccess(user);
};

const handleTokenFetchFailure = async (lastError: any): Promise<never> => {
  console.error("Token fetch failed:", lastError);
  
  // Show error and navigate to 401 - no Okta redirect to avoid loops
  if (dispatchInstance && navigateInstance) {
    dispatchInstance(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        "Login failed. Please try again later or contact support.",
        Date.now().toString()
      )
    );
    navigateInstance("/401");
  }
  throw lastError ?? new Error("Token fetch failed");
};

const fetchAndStoreToken = async (): Promise<string> => {
  if (!oktaAuthInstance || !dispatchInstance) {
    throw new Error("Interceptor not properly initialized");
  }
  if (isRefreshingToken && tokenRefreshPromise) {
    console.log("Token refresh already in progress, waiting...");
    return tokenRefreshPromise;
  }
  isRefreshingToken = true;
  tokenRefreshPromise = tryFetchToken().catch(async (error) => {
    await handleTokenFetchFailure(error);
    throw error;
  });
  try {
    return await tokenRefreshPromise;
  } finally {
    isRefreshingToken = false;
    tokenRefreshPromise = null;
  }
};

/**
 * Get JWT token from localStorage
 * @returns JWT token string or null if not found
 */
const getStoredToken = (): string | null => {
  const authUser = getUserInfo();
  return authUser?.jwtToken ?? null;
};

/**
 * Validate and refresh token if needed
 * Returns valid token ready to use with caching and validation
 * @param requestUrl - URL of the request (for logging purposes)
 * @returns Promise that resolves to valid JWT token string
 * @throws Error if token cannot be validated or refreshed
 */
const ensureValidToken = async (requestUrl: string): Promise<string> => {
  if (!oktaAuthInstance) {
    throw new Error("OktaAuth instance not initialized");
  }

  let token = getStoredToken();

  // Case 1: No token - fetch new one
  if (!token) {
    console.warn(AUTH_ERROR_MESSAGES.TOKEN_MISSING + ". Fetching new token...");
    try {
      token = await fetchAndStoreToken();
    } catch (error) {
      console.error("Token fetch failed:", error);
      throw new Error(AUTH_ERROR_MESSAGES.TOKEN_FETCH_FAILED);
    }
    return token;
  }

  // Case 2: Token exists - check if validation is cached
  if (isValidationCacheFresh()) {
    // Token was validated recently, use it directly
    return token;
  }

  // Case 3: Validation cache expired - perform checks
  try {
    // Check token structure
    if (!isTokenStructureValid(token)) {
      console.warn(AUTH_ERROR_MESSAGES.TOKEN_INVALID + ". Fetching new token...");
      token = await fetchAndStoreToken();
      return token;
    }

    // Check token expiration
    const tokenStatus = isTokenExpired(token, TOKEN_CONFIG.EXPIRY_BUFFER_MINUTES);

    // Special case: token expired more than 24 hours ago ("expiredYesterday")
    // We force a clean Okta re-login rather than trying to refresh.
    if (tokenStatus === "expiredYesterday") {
      await redirectToOktaLogin(oktaAuthInstance, AUTH_ERROR_MESSAGES.TOKEN_EXPIRED_YESTERDAY);
      throw new Error(AUTH_ERROR_MESSAGES.TOKEN_EXPIRED_YESTERDAY);
    }

    // Generic case: expired or expiring soon (boolean true) => refresh
    if (tokenStatus === true) {
      console.warn(AUTH_ERROR_MESSAGES.TOKEN_EXPIRED + ". Refreshing...");
      token = await fetchAndStoreToken();
      return token;
    }

    // Token is valid - update cache time
    lastTokenValidationTime = Date.now();
    return token;
    
  } catch (error) {
    console.error("Failed to validate or refresh JWT token:", error);
    // If validation fails due to auth issue, redirect to login
    if (error instanceof Error && error.message === AUTH_ERROR_MESSAGES.USER_NOT_AUTHENTICATED) {
      await redirectToOktaLogin(oktaAuthInstance, `${AUTH_ERROR_MESSAGES.USER_NOT_AUTHENTICATED} during token validation`);
    }
    
    throw new Error(AUTH_ERROR_MESSAGES.TOKEN_VALIDATION_FAILED);
  }
};

/**
 * Check if error message indicates database user validation issue
 * @param message - Error message from API response
 * @returns true if message matches known DB user error patterns
 */
const isDBUserError = (message: string): boolean => {
  if (!message) return false;
  const lowerMessage = message.toLowerCase();
  return DB_USER_ERROR_PATTERNS.some(err => lowerMessage.includes(err));
};

/**
 * Handle database user validation errors
 * @param errorMessage - Raw error message from API
 * @param url - Request URL that failed
 */
const handleDBUserError = async (errorMessage: string, url: string): Promise<void> => {
  if (!dispatchInstance || !navigateInstance) return;

  console.error("Login Database Level error:", errorMessage);
  await Log({
    errorMessage: `Front-end API failed: ${JSON.stringify(errorMessage)}`,
  });
  dispatchInstance(
    PopoutNotification(
      NOTIFICATION_TYPE.ERROR,
      typeof errorMessage === "string" ? errorMessage : ErrorMessages.dbUserInfoMissing,
      Date.now().toString()
    )
  );
  navigateInstance("/401");
};

/**
 * Handle 401 unauthorized errors with specific error messages
 * @param errorMessage - Lowercase error message from API
 * @param url - Request URL that failed
 * @param error - Original error object
 */
const handle401Error = async (errorMessage: string, url: string, error: any): Promise<void> => {
  if (!dispatchInstance || !navigateInstance) return;

  const lowerMessage = errorMessage.toLowerCase();

  if (lowerMessage.includes("user is not active")) {
    console.error("OKTA user not active");
    dispatchInstance(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        ErrorMessages.userNotActive,
        Date.now().toString()
      )
    );
    navigateInstance("/401");
    return;
  }

  if (lowerMessage.includes("the user email is missing")) {
    console.error("User email is missing");
    await Log({
      errorMessage: `Front-end API failed, url: ${url} - Email missing`,
      error,
    });
    dispatchInstance(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        typeof errorMessage === "string" ? errorMessage : ErrorMessages.userEmailMissing,
        Date.now().toString()
      )
    );
    navigateInstance("/401");
    return;
  }

  if (lowerMessage.includes("jwt expired")) {
    console.error("JWT Token expired in API response");
    dispatchInstance(
      PopoutNotification(
        NOTIFICATION_TYPE.ERROR,
        "Session expired. Please refresh the page.",
        Date.now().toString()
      )
    );
    navigateInstance("/401");
    return;
  }

  // Generic 401 error
  console.error("Unauthorized access");
  navigateInstance("/401");
};

/**
 * Handle 503 service unavailable errors (deployment in progress)
 * @param headers - Response headers
 * @returns true if deployment is in progress and redirect occurred
 */
const handle503Error = (headers: any): boolean => {
  if (!navigateInstance) return false;

  const deploymentStatus = headers?.get?.("X-Deployment-Status") ?? headers?.["x-deployment-status"];
  
  if (deploymentStatus) {
    console.error(AUTH_ERROR_MESSAGES.DEPLOYMENT_IN_PROGRESS);
    navigateInstance("/maintenance");
    return true;
  }
  
  return false;
};

// ============================================================================
// Cross-Tab Synchronization
// ============================================================================

/**
 * Handle storage events for cross-tab token synchronization
 * When token is updated in another tab, clear validation cache
 */
const handleStorageChange = (event: StorageEvent): void => {
  if (event.key === STORAGE_KEYS.AUTH_USER && event.newValue !== event.oldValue) {
    console.log("Token updated in another tab, clearing validation cache");
    clearValidationCache();
  }
};

/**
 * Handle page visibility change
 * Clear validation cache when page becomes visible to force revalidation
 */
const handleVisibilityChange = (): void => {
  if (document.visibilityState === 'visible') {
    console.log("Page became visible, clearing validation cache for fresh token check");
    clearValidationCache();
  }
};

/**
 * Setup event listeners for cross-tab sync and visibility changes
 */
const setupEventListeners = (): void => {
  // Listen for storage events (cross-tab communication)
  globalThis.addEventListener('storage', handleStorageChange);
  
  // Listen for visibility changes
  document.addEventListener('visibilitychange', handleVisibilityChange);
};

/**
 * Remove event listeners
 */
const cleanupEventListeners = (): void => {
  globalThis.removeEventListener('storage', handleStorageChange);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
};

/**
 * Cleanup all interceptor resources and event listeners
 * Call this when unmounting the app or cleaning up interceptors
 */
export const cleanupAuthInterceptors = (): void => {
  cleanupEventListeners();
  oktaAuthInstance = null;
  dispatchInstance = null;
  navigateInstance = null;
  isInterceptorInitialized = false;
  // Reset circuit breaker state
  networkErrorCount = 0;
  circuitBreakerOpenUntil = 0;
  lastNetworkErrorNotificationTime = 0;
  resetInterceptorReady();
  console.log("Cleaned up auth interceptors and event listeners");
};

// ============================================================================
// Interceptor Setup
// ============================================================================


/**
 * Initialize Axios authentication interceptors
 * Handles JWT token validation, refresh, and error responses
 * Sets up cross-tab synchronization and visibility change handling
 * @param oktaAuth - OktaAuth instance from Okta React SDK
 * @param dispatch - Redux dispatch function
 * @param navigate - React Router navigate function
 */

function setupRequestInterceptor() {
  axios.interceptors.request.use(
    async (request) => {
      // Check circuit breaker before making request
      if (isCircuitBreakerOpen()) {
        const remainingTime = Math.ceil((circuitBreakerOpenUntil - Date.now()) / 1000);
        console.warn(`Circuit breaker open - request blocked. Retry in ${remainingTime}s. URL: ${request.url}`);
        throw new Error(`Server unavailable. Please wait ${remainingTime} seconds before retrying.`);
      }
      
      if (isExcludedEndpoint(request.url)) {
        return request;
      }
      try {
        const jwtToken = await ensureValidToken(request.url ?? "unknown");
        _.set(request, "headers.jwt_token", jwtToken);
        _.set(request, "headers.OSD-UI-Url", globalThis.location.pathname);
        return request;
      } catch (error) {
        console.error("Request interceptor error:", error);
        throw error;
      }
    },
    (error) => {
      console.error("Request interceptor error:", error);
      throw error;
    }
  );
}

function dispatchErrorNotification(message: string) {
  dispatchInstance?.(
    PopoutNotification(
      NOTIFICATION_TYPE.ERROR,
      message,
      Date.now().toString()
    )
  );
}

function handleNetworkError(error: Error & { code?: string }) {
  console.error("Network error - no response from server:", error.message);
  
  // Record the network error for circuit breaker
  recordNetworkError();
  
  // Only show notification if debounce period has passed
  if (shouldShowErrorNotification()) {
    if (error.code === "ECONNABORTED") {
      console.error("Request timeout");
      dispatchErrorNotification(AUTH_ERROR_MESSAGES.REQUEST_TIMEOUT);
    } else if (error.message === "Network Error") {
      console.error("Network connectivity issue - server may be down");
      dispatchErrorNotification(AUTH_ERROR_MESSAGES.NETWORK_ERROR);
    } else {
      console.error("Unknown network error");
    }
  } else {
    console.log("Network error notification suppressed (debounced)");
  }
  
  throw error;
}

async function handle403Error(originalRequest: AxiosRequestConfig, error: unknown) {
  console.error("Access forbidden - insufficient permissions");
  await Log({
    errorMessage: `Front-end API failed, url: ${originalRequest?.url ?? "unknown"} - Forbidden (403)`,
    error,
  });
  dispatchErrorNotification(AUTH_ERROR_MESSAGES.ACCESS_DENIED);
  navigateInstance?.("/401");
  throw error;
}

function setupResponseInterceptor() {
  axios.interceptors.response.use(
    (response) => {
      // Reset network error count on successful response
      resetNetworkErrorCount();
      return response;
    },
    async (error) => {
      const { config: originalRequest, response } = error;
      if (!response) {
        handleNetworkError(error);
      }
      
      // If we got a response, network is working - reset error count
      resetNetworkErrorCount();
      
      const status = _.get(response, "status");
      if (!status) {
        console.error("No status code in response");
        throw error;
      }
      const rawErrorMessage =
        response?.data?.error?.message ??
        response?.data?.errors?.[0]?.message ??
        "";
      const errorMessage = rawErrorMessage.toLowerCase?.() ?? "";
      if (status === 503 && handle503Error(response.headers)) {
        throw new Error("Deployment in progress");
      }
      if (isDBUserError(errorMessage)) {
        await handleDBUserError(rawErrorMessage, originalRequest?.url ?? "unknown");
        throw error;
      }
      if (status === 403) {
        await handle403Error(originalRequest, error);
      }
      if (status === 401) {
        await handle401Error(errorMessage, originalRequest?.url ?? "unknown", error);
        throw error;
      }
      console.error("API error:", errorMessage ?? error.message);
      throw error;
    }
  );
}

export const initAxiosAuthInterceptors = async (
  oktaAuth: OktaAuth,
  dispatch: Dispatch,
  navigate: NavigateFunction
) => {
  if (isInterceptorInitialized) {
    console.warn("Interceptors already initialized, skipping");
    return;
  }
  oktaAuthInstance = oktaAuth;
  dispatchInstance = dispatch;
  navigateInstance = navigate;
  setupEventListeners();
  console.log("Initializing axios auth interceptors with cross-tab sync");
  setupRequestInterceptor();
  setupResponseInterceptor();
  isInterceptorInitialized = true;

  // Eagerly pre-fetch and store JWT token ONLY if Okta auth state is ready and authenticated
  // On page reload, Okta SDK may not have restored tokens yet (auth state is null/isPending)
  // In that case, skip eager fetch - let RequiredAuth handle authentication flow
  const authState = oktaAuth?.authStateManager?.getAuthState();
  const isOktaReady = authState !== null && authState !== undefined && !authState.isPending;
  const isOktaAuthenticated = authState?.isAuthenticated === true;
  
  if (isOktaReady && isOktaAuthenticated) {
    try {
      await fetchAndStoreToken();
      console.log("JWT token pre-fetched successfully");
    } catch (error) {
      console.error("Failed to pre-fetch JWT token:", error);
      // Do NOT throw - proceed to setInterceptorReady() so requests are never blocked forever
      // The existing ensureValidToken logic will handle retry if pre-fetch failed
    }
  } else {
    console.log("Skipping eager JWT fetch - Okta auth state not ready or not authenticated yet");
    console.log(`Auth state ready: ${isOktaReady}, Authenticated: ${isOktaAuthenticated}`);
  }

  setInterceptorReady();
  console.log("Auth interceptor ready - API requests can now proceed");
};
